import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from '@noor/lib';
import {
  CHAT_INVALIDATION_THROTTLE_MS,
  chatSchema,
  conversationsKeys,
} from '@/entities/conversation';
import { showChatNotification } from '../lib/browser-notification';
import { usePendingOfferStore } from './pending-offer-store';

/**
 * The chat pushes of the admin socket, structurally the same as the socket's
 * `AdminSocketChatAction` (the feature layer must not import app infrastructure).
 */
export type ChatRealtimeEvent =
  | { kind: 'chat-message'; content: Record<string, unknown> }
  | { kind: 'new-chat'; content: Record<string, unknown> }
  | { kind: 'chat-status' }
  | { kind: 'messages-seen' };

/** Roles that do not use chat at all (cpanel `AF_CHAT_DISABLED_ROLES`). */
const CHAT_DISABLED_ROLES = ['AF_MANAGER', 'AF_ADMIN'] as const;

/** Roles that get a desktop ping for an incoming message. */
const NOTIFIED_ROLES = ['CALL_CENTER', 'MANAGER'] as const;

function readChatId(content: Record<string, unknown>): number | undefined {
  const raw = content.chat_id;
  const id = typeof raw === 'string' ? Number(raw) : raw;
  return typeof id === 'number' && Number.isFinite(id) ? id : undefined;
}

/**
 * Realtime handling for the support inbox (cpanel's `WebSocketProvider` chat
 * branch). Returns the `onChatEvent` the admin socket calls:
 *
 * - `chat_message` — refetch that chat's thread right away, refresh the lists
 *   (throttled), and ping CALL_CENTER / MANAGER operators;
 * - `new_chat` — remember the hand-over offer for the accept dialog, toast
 *   "needs help", refresh the lists;
 * - `new_chat_status` — refresh the lists + open chat;
 * - `messages_has_been_seen` — refetch threads and chat details (read ticks).
 *
 * List refreshes are throttled to one per 5 s like cpanel, with a trailing
 * refresh so the last event of a burst is never lost.
 */
export function useChatRealtime(role: string | null) {
  const { t } = useTranslation('inbox');
  const queryClient = useQueryClient();
  const throttleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trailingRef = useRef(false);

  useEffect(
    () => () => {
      if (throttleRef.current) clearTimeout(throttleRef.current);
    },
    [],
  );

  const refreshLists = useCallback(() => {
    if (throttleRef.current) {
      trailingRef.current = true;
      return;
    }
    void queryClient.invalidateQueries({ queryKey: conversationsKeys.lists() });
    throttleRef.current = setTimeout(() => {
      throttleRef.current = null;
      if (trailingRef.current) {
        trailingRef.current = false;
        void queryClient.invalidateQueries({ queryKey: conversationsKeys.lists() });
      }
    }, CHAT_INVALIDATION_THROTTLE_MS);
  }, [queryClient]);

  const normalizedRole = role?.toUpperCase() ?? '';
  const disabled = (CHAT_DISABLED_ROLES as readonly string[]).includes(normalizedRole);
  const notified = (NOTIFIED_ROLES as readonly string[]).includes(normalizedRole);

  return useCallback(
    (event: ChatRealtimeEvent) => {
      if (disabled) return;

      switch (event.kind) {
        case 'chat-message': {
          const chatId = readChatId(event.content);
          refreshLists();
          if (chatId !== undefined) {
            void queryClient.invalidateQueries({
              queryKey: conversationsKeys.thread(chatId),
            });
            void queryClient.invalidateQueries({
              queryKey: conversationsKeys.detail(chatId),
            });
          }
          if (notified) {
            const message =
              typeof event.content.message === 'string' ? event.content.message : '';
            showChatNotification(t('notification.newMessage'), message);
          }
          break;
        }
        case 'new-chat': {
          const parsed = chatSchema.safeParse(event.content);
          if (parsed.success) usePendingOfferStore.getState().setOffer(parsed.data);
          toast.info(t('notification.needsHelp'));
          refreshLists();
          break;
        }
        case 'chat-status':
          refreshLists();
          void queryClient.invalidateQueries({ queryKey: conversationsKeys.details() });
          break;
        case 'messages-seen':
          void queryClient.invalidateQueries({ queryKey: conversationsKeys.threads() });
          void queryClient.invalidateQueries({ queryKey: conversationsKeys.details() });
          break;
      }
    },
    [disabled, notified, queryClient, refreshLists, t],
  );
}
